import { GetIssueResponseDto } from '@issuemanager/api-client';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import {
  IssueStatusActionable,
  IssueStatusEnum,
} from '@issuemanager/constants';
import { useMemo } from 'react';

export interface UseFieldStateParams {
  issue?: GetIssueResponseDto | null;
  userRoles?: {
    isCreator?: boolean;
    isIssueTypeManager?: boolean;
    isManager?: boolean;
    isOwner?: boolean;
  } | null;
  fieldName: string;
  isReadOnly?: boolean;
}

export interface FieldState {
  editable: boolean;
  visible: boolean;
}

export const useFieldState = ({
  issue,
  userRoles,
  fieldName,
  isReadOnly = false,
}: UseFieldStateParams): FieldState => {
  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');

  return useMemo(() => {
    if (!issue || !userRoles) {
      return { editable: false, visible: false };
    }

    const isDraft =
      isDraftIssuesEnabled && issue.status === IssueStatusEnum.DRAFT;

    const isCreator = Boolean(userRoles.isCreator);
    const isTypeManager = Boolean(userRoles.isIssueTypeManager);
    const isManager = Boolean(userRoles.isManager);
    const isOwner = Boolean(userRoles.isOwner);

    const isReporter = isCreator && !isTypeManager && !isManager && !isOwner;
    const canManageIssues = isTypeManager || isManager;
    const canManageDraft = isCreator || canManageIssues;

    const calculateVisibility = (): boolean => {
      switch (fieldName) {
        default:
          return true;
      }
    };

    const calculateEditable = (): boolean => {
      if (isReadOnly) return false;

      if (fieldName === 'title') {
        return Boolean(isDraft && canManageDraft);
      }

      if (['managerId', 'approverId'].includes(fieldName)) {
        if (isReporter) {
          return false;
        }
        return canManageIssues;
      }

      if (fieldName === 'impact') {
        if (isDraft && isCreator) {
          return true;
        }
        if (
          canManageIssues &&
          issue.status &&
          IssueStatusActionable.includes(issue.status as IssueStatusEnum)
        ) {
          return true;
        }
        return false;
      }

      if (
        [
          'reviewStatus',
          'datePublished',
          'resolution',
          'managementResponse',
          'remediation',
        ].includes(fieldName)
      ) {
        return canManageIssues;
      }

      if (
        [
          'classification',
          'rootCause',
          'recommendations',
          'includeInAuditReport',
        ].includes(fieldName)
      ) {
        if (isDraft) {
          return canManageDraft;
        }
        return canManageIssues || isReporter;
      }

      if (isDraft) {
        return canManageDraft;
      }
      return canManageIssues;
    };

    return {
      visible: calculateVisibility(),
      editable: calculateEditable(),
    };
  }, [issue, userRoles, fieldName, isReadOnly, isDraftIssuesEnabled]);
};
